import React from 'react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { AlertType } from './helpers';
import useStyles from '../../styles/alertForm';
import {
  LocationAlertParamAlertMethodsInner,
  EmailAlertMethod,
  WebhookAlertMethod,
  WebHookParams,
} from '../../services/api';

interface AlertMethodsFormProps {
  value: LocationAlertParamAlertMethodsInner[];
  onChange: (methods: LocationAlertParamAlertMethodsInner[]) => void;
  allowEditing: boolean;
}

// Type guards to narrow the discriminated union
function isEmailMethod(
  method: LocationAlertParamAlertMethodsInner
): method is { type: 'email' } & EmailAlertMethod {
  return method.type === 'email';
}

function isWebhookMethod(
  method: LocationAlertParamAlertMethodsInner
): method is { type: 'webhook' } & WebhookAlertMethod {
  return method.type === 'webhook';
}

function AlertMethodsFormSection({ value, onChange, allowEditing }: AlertMethodsFormProps) {
  const { classes } = useStyles();

  const addMethod = () => {
    const newMethod = {
      type: 'webhook',
      params: { url: '' } as WebHookParams,
    } as unknown as LocationAlertParamAlertMethodsInner;
    onChange([...value, newMethod]);
  };

  const removeMethod = (index: number) => {
    onChange(value.filter((_, i) => i !== index));
  };

  const updateMethodType = (index: number, newType: AlertType) => {
    const newMethods = value.map((method, i) => {
      if (i !== index) return method;

      if (newType === AlertType.email) {
        return {
          type: 'email',
          recipients: [''],
          sender: '',
        } as unknown as LocationAlertParamAlertMethodsInner;
      }

      return {
        type: 'webhook',
        params: { url: '' } as WebHookParams,
      } as unknown as LocationAlertParamAlertMethodsInner;
    });
    onChange(newMethods);
  };

  // Change patch type to target the nested params object
  const updateEmailField = (index: number, patch: Partial<EmailAlertMethod['params']>) => {
    const newMethods = value.map((method, i) => {
      if (i !== index || !isEmailMethod(method)) return method;
      return { ...method, params: { ...method.params, ...patch } }; // merge into params
    });
    onChange(newMethods);
  };

  const updateWebhookField = (index: number, patch: Partial<WebHookParams>) => {
    const newMethods = value.map((method, i) => {
      if (i !== index || !isWebhookMethod(method)) return method;
      return { ...method, params: { ...method.params, ...patch } };
    });
    onChange(newMethods);
  };

  return (
    <Box className={classes.formContainer}>
      <InputLabel sx={{ mb: 2 }}>Select Alert methods</InputLabel>
      {value.map((method, index) => (
        <Stack
          // eslint-disable-next-line react/no-array-index-key
          key={`${method.type}-${index}`}
          direction="row"
          spacing={1.5}
          sx={{ margin: '10px 0' }}
          alignItems="flex-start"
        >
          <FormControl size="small" sx={{ minWidth: 130 }}>
            <InputLabel>Type</InputLabel>
            <Select
              label="Type"
              variant="standard"
              value={method.type}
              onChange={(e) => updateMethodType(index, e.target.value as AlertType)}
              disabled={!allowEditing}
            >
              {Object.values(AlertType).map((t) => (
                <MenuItem key={t} value={t}>
                  {t}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {isWebhookMethod(method) && (
            <TextField
              fullWidth
              variant="standard"
              size="small"
              label="URL"
              value={method.params.url}
              onChange={(e) => updateWebhookField(index, { url: e.target.value })}
              placeholder="https://..."
              disabled={!allowEditing}
              error={method.params.url === 'https://'}
            />
          )}

          {isEmailMethod(method) && (
            <Stack direction="column" spacing={1} flexGrow={1}>
              <TextField
                fullWidth
                variant="standard"
                size="small"
                label="Recipients (comma-separated)"
                value={(method.params?.recipients ?? []).join(',')}
                onChange={(e) =>
                  updateEmailField(index, {
                    recipients: e.target.value.split(',').map((s) => s.trim()),
                  })
                }
                placeholder="recipient1@example.com, recipient2@example.com"
                disabled={!allowEditing}
              />
              <TextField
                fullWidth
                variant="standard"
                size="small"
                label="CC addresses (optional - comma-separated)"
                value={(method.params?.CcAddresses ?? []).join(',') ?? ''}
                onChange={(e) =>
                  updateEmailField(index, {
                    CcAddresses: e.target.value.split(',').map((s) => s.trim()),
                  })
                }
                placeholder="cc@example.com, cc2@example.com"
                disabled={!allowEditing}
              />
              <TextField
                fullWidth
                variant="standard"
                size="small"
                label="BCC addresses (optional - comma-separated)"
                value={(method.params?.BccAddresses ?? []).join(',') ?? ''}
                onChange={(e) =>
                  updateEmailField(index, {
                    BccAddresses: e.target.value.split(',').map((s) => s.trim()),
                  })
                }
                placeholder="bcc@example.com, bcc2@example.com"
                disabled={!allowEditing}
              />
            </Stack>
          )}

          {value.length > 0 && (
            <IconButton
              size="small"
              onClick={() => removeMethod(index)}
              color="error"
              sx={{ mt: 0.5 }}
            >
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          )}

          <Tooltip title="Add Method">
            <IconButton
              aria-label="Add threshold"
              size="small"
              onClick={addMethod}
              disabled={!allowEditing}
              sx={{ visibility: `${index + 1 === value.length ? 'visible' : 'hidden'}` }}
            >
              <AddIcon fontSize="small" color="success" />
            </IconButton>
          </Tooltip>
        </Stack>
      ))}
    </Box>
  );
}

export default AlertMethodsFormSection;
